/**
 * Invoices Management Page
 * Displays all invoices across brands with generation, approval, and sending capabilities
 */

import React, { useState, useMemo, useCallback, useEffect, act } from 'react';
import {
  Page,
  Layout,
  Card,
  Text,
  Box,
  BlockStack,
  InlineStack,
  Badge,
  Button,
  Spinner,
  Banner,
  EmptyState,
  IndexTable,
  Tooltip,
  Modal,
  TextField,
  Select,
  FormLayout,
  Divider,
} from '@shopify/polaris';
import {
  RefreshIcon,
  EmailIcon,
  CheckIcon,
  CashDollarIcon,
  DeleteIcon,
  ReceiptIcon,
  CalendarIcon,
  ViewIcon,
} from '@shopify/polaris-icons';

import { useBrands } from '../utils/queries';
import apiClient from '../utils/api';
import "../style.css";

/**
 * Mask sensitive account numbers - show only last 4 digits
 * @param {string} accountNumber - The account number to mask
 * @returns {string} Masked account number (e.g., "****1234")
 */
const maskAccountNumber = (accountNumber) => {
  if (!accountNumber || typeof accountNumber !== 'string') return '-';
  if (accountNumber.length <= 4) return accountNumber;
  return '****' + accountNumber.slice(-4);
};

export default function Invoices() {
  // State management
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Filter states
  const [brandFilter, setBrandFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [monthFilter, setMonthFilter] = useState(() => {
    const now = new Date();
    const prevMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    return `${prevMonth.getFullYear()}-${(prevMonth.getMonth() + 1).toString().padStart(2, '0')}`;
  });
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState('descending');

  // Modal states
  const [generateModalActive, setGenerateModalActive] = useState(false);
  const [detailsModalActive, setDetailsModalActive] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [pdfDownloadingId, setPdfDownloadingId] = useState(null); // Track which invoice PDF is being downloaded

  // Trigger monthly invoices modal form state
  const [triggerForm, setTriggerForm] = useState({
    monthString: '', // Format: YYYY-MM
  });

  // Send invoice modal state
  const [sendModalActive, setSendModalActive] = useState(false);

  // Payment modal state
  const [paymentModalActive, setPaymentModalActive] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    paymentMethod: '',
    reference: ''
  });

  const itemsPerPage = 20;

  // Fetch brands for dropdown
  const { data: brandsResponse, isLoading: brandsLoading } = useBrands();
  const brands = brandsResponse?.brands || brandsResponse || [];

  // Fetch invoices
  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const params = {
        page: currentPage,
        limit: itemsPerPage,
        sortBy,
        sortDirection,
      };

      if (brandFilter) params.brandId = brandFilter;
      if (statusFilter) params.status = statusFilter;
      if (monthFilter) params.month = monthFilter;

      const response = await apiClient.getInvoices(params);

      if (response.success) {
        setInvoices(response.invoices || []);
        setTotalPages(response.pagination?.totalPages || 1);
      } else {
        setError(response.error || 'Failed to load invoices');
      }
    } catch (err) {
      console.error('Error fetching invoices:', err);
      setError(err.message || 'Failed to load invoices');
    } finally {
      setLoading(false);
    }
  }, [currentPage, brandFilter, statusFilter, monthFilter, sortBy, sortDirection]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  // Brand options for filter/generate
  const brandOptions = useMemo(() => {
    return [
      { label: 'All Brands', value: '' },
      ...brands.map(brand => ({
        label: brand.name,
        value: brand.id
      }))
    ];
  }, [brands]);

  // Status options
  const statusOptions = [
    { label: 'All Statuses', value: '' },
    { label: 'Draft', value: 'DRAFT' },
    { label: 'Approved', value: 'APPROVED' },
    { label: 'Sent', value: 'SENT' },
    { label: 'Paid', value: 'PAID' },
    { label: 'Cancelled', value: 'CANCELLED' },
  ];

  // Month options (last 24 months)
  const monthOptions = useMemo(() => {
    const options = [{ label: 'All Months', value: '' }];
    const now = new Date();
    for (let i = 0; i < 24; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthString = `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}`;
      const monthLabel = date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
      options.push({ label: monthLabel, value: monthString });
    }
    return options;
  }, []);

  // Get status badge tone
  const getStatusBadge = (status) => {
    const statusConfig = {
      DRAFT: { tone: 'attention', label: 'Draft' },
      APPROVED: { tone: 'info', label: 'Approved' },
      SENT: { tone: 'warning', label: 'Sent' },
      PAID: { tone: 'success', label: 'Paid' },
      CANCELLED: { tone: 'critical', label: 'Cancelled' },
    };
    const config = statusConfig[status] || { tone: 'default', label: status };
    return <Badge tone={config.tone}>{config.label.toLowerCase() === "sent" ? "Approved/Awaiting Payment" : config.label}</Badge>;
  };

  // Format currency
  const formatCurrency = (amount) => {
    const num = parseFloat(amount) || 0;
    return `USD ${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Format negative currency (puts minus after USD: USD -1,234.56)
  const formatNegativeCurrency = (amount) => {
    const num = parseFloat(amount) || 0;
    return `USD ${num != 0 ? '-' : ''}${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Format date (using UTC to prevent timezone shifts)
  const formatDate = (date) => {
    if (!date) return '-';
    const d = new Date(date);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${months[d.getUTCMonth()]} ${d.getUTCDate()}, ${d.getUTCFullYear()}`;
  };

  // Handle trigger monthly invoices (auto-generation for all configured brands)
  const handleTriggerMonthlyInvoices = async () => {
    setActionLoading(true);
    setError('');

    try {
      const response = await apiClient.triggerMonthlyInvoices({
        monthString: triggerForm.monthString || null, // null = previous month
      });

      if (response.success) {
        setSuccessMessage(response.message || 'Monthly invoice generation triggered successfully');
        setGenerateModalActive(false);
        setTriggerForm({ monthString: '' });
        // Invoices will appear after the background job completes
        setTimeout(() => fetchInvoices(), 2000); // Refresh after 2 seconds
      } else {
        setError(response.error || 'Failed to trigger invoice generation');
      }
    } catch (err) {
      console.error('Error triggering monthly invoices:', err);
      setError(err.message || 'Failed to trigger invoice generation');
    } finally {
      setActionLoading(false);
    }
  };

  // Handle approve invoice
  const handleApproveInvoice = async (invoiceId) => {
    setActionLoading(invoiceId);
    setError('');

    try {
      const response = await apiClient.approveInvoice(invoiceId);

      if (response.success) {
        setSuccessMessage('Invoice approved successfully');
        fetchInvoices();
        if (detailsModalActive) {
          setSelectedInvoice(response.invoice);
        }
      } else {
        setError(response.error || 'Failed to approve invoice');
      }
    } catch (err) {
      console.error('Error approving invoice:', err);
      setError(err.message || 'Failed to approve invoice');
    } finally {
      setActionLoading(false);
    }
  };

  // Handle record payment
  const handleRecordPayment = async () => {
    if (!selectedInvoice) return;

    setActionLoading(true);
    setError('');

    try {
      const response = await apiClient.recordInvoicePayment(selectedInvoice.id, paymentForm);

      if (response.success) {
        setSuccessMessage('Payment recorded successfully');
        setPaymentModalActive(false);
        setPaymentForm({ paymentMethod: '', reference: '' });
        fetchInvoices();
        if (detailsModalActive) {
          setSelectedInvoice(response.invoice);
        }
      } else {
        setError(response.error || 'Failed to record payment');
      }
    } catch (err) {
      console.error('Error recording payment:', err);
      setError(err.message || 'Failed to record payment');
    } finally {
      setActionLoading(false);
    }
  };

  // Handle cancel invoice
  const handleCancelInvoice = async (invoiceId) => {
    if (!confirm('Are you sure you want to cancel this invoice?')) return;

    setActionLoading(true);
    setError('');

    try {
      const response = await apiClient.cancelInvoice(invoiceId);

      if (response.success) {
        setSuccessMessage('Invoice cancelled');
        fetchInvoices();
        if (detailsModalActive) {
          setDetailsModalActive(false);
          setSelectedInvoice(null);
        }
      } else {
        setError(response.error || 'Failed to cancel invoice');
      }
    } catch (err) {
      console.error('Error cancelling invoice:', err);
      setError(err.message || 'Failed to cancel invoice');
    } finally {
      setActionLoading(false);
    }
  };

  // Handle delete invoice
  const handleDeleteInvoice = async (invoiceId) => {
    if (!confirm('Are you sure you want to delete this invoice? This action cannot be undone.')) return;

    setActionLoading(true);
    setError('');

    try {
      const response = await apiClient.deleteInvoice(invoiceId);

      if (response.success) {
        setSuccessMessage('Invoice deleted successfully');
        fetchInvoices();
        if (detailsModalActive) {
          setDetailsModalActive(false);
          setSelectedInvoice(null);
        }
      } else {
        setError(response.error || 'Failed to delete invoice');
      }
    } catch (err) {
      console.error('Error deleting invoice:', err);
      setError(err.message || 'Failed to delete invoice');
    } finally {
      setActionLoading(false);
    }
  };

  // Handle regenerate invoice (only DRAFT)
  const handleRegenerateInvoice = async (invoiceId) => {
    if (!confirm('Are you sure you want to regenerate this invoice? This will update the invoice with the latest billing data.')) return;

    setActionLoading(true);
    setError('');

    try {
      const response = await apiClient.regenerateInvoice(invoiceId);

      if (response.success) {
        setSuccessMessage('Invoice regenerated successfully');
        fetchInvoices();
        if (detailsModalActive) {
          setSelectedInvoice(response.invoice);
        }
      } else {
        setError(response.error || 'Failed to regenerate invoice');
      }
    } catch (err) {
      console.error('Error regenerating invoice:', err);
      setError(err.message || 'Failed to regenerate invoice');
    } finally {
      setActionLoading(false);
    }
  };

  // Handle download PDF with loading state
  const handleDownloadPdf = async (invoiceId, invoiceNumber) => {
    try {
      setPdfDownloadingId(invoiceId);
      const blob = await apiClient.downloadInvoicePdf(invoiceId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoice-${invoiceNumber}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Error downloading PDF:', err);
      setError('Failed to download PDF');
    } finally {
      setPdfDownloadingId(null);
    }
  };

  // View invoice details
  const handleViewDetails = async (invoice) => {
    try {
      const response = await apiClient.getInvoice(invoice.id);
      if (response.success) {
        setSelectedInvoice(response.invoice);
        setDetailsModalActive(true);
      }
    } catch (err) {
      console.error('Error fetching invoice details:', err);
      setError('Failed to load invoice details');
    }
  };

  // Table rows
  const rowMarkup = invoices.map((invoice, index) => (
    <IndexTable.Row
      id={invoice.id}
      key={invoice.id}
      position={index}
    >
      <IndexTable.Cell>
        <Button variant="plain" onClick={() => handleViewDetails(invoice)}>
          {invoice.invoiceNumber}
        </Button>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Text variant="bodyMd">{invoice.brand?.name || '-'}</Text>
      </IndexTable.Cell>
      <IndexTable.Cell>
        {getStatusBadge(invoice.status)}
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Text variant="bodyMd">{formatDate(invoice.periodStart)} - {formatDate(invoice.periodEnd)}</Text>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Text variant="bodyMd" fontWeight="semibold">{formatCurrency(invoice.totalAmount)}</Text>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Text variant="bodyMd">{formatDate(invoice.issueDate)}</Text>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Text variant="bodyMd">{formatDate(invoice.dueDate)}</Text>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <InlineStack gap="200">
          <Tooltip content="View Details">
            <Button
              icon={ViewIcon}
              variant="plain"
              onClick={() => handleViewDetails(invoice)}
              accessibilityLabel="View details"
            />
          </Tooltip>
          <Tooltip content="Download PDF">
            <Button
              icon={ReceiptIcon}
              variant="plain"
              onClick={() => handleDownloadPdf(invoice.id, invoice.invoiceNumber)}
              accessibilityLabel="Download PDF"
              loading={pdfDownloadingId === invoice.id}
              disabled={pdfDownloadingId !== null}
            />
          </Tooltip>
          {invoice.status === 'DRAFT' && (
            <Tooltip content="Regenerate">
              <Button
                icon={RefreshIcon}
                variant="plain"
                onClick={() => handleRegenerateInvoice(invoice.id)}
                accessibilityLabel="Regenerate invoice"
              />
            </Tooltip>
          )}
          {invoice.status === 'DRAFT' && (
            <Tooltip content="Approve">
              <Button
                icon={CheckIcon}
                variant="plain"
                tone="success"
                onClick={() => handleApproveInvoice(invoice.id)}
                accessibilityLabel="Approve invoice"
                loading={actionLoading == invoice.id}
              />
            </Tooltip>
          )}
          {(invoice.status === 'SENT' || invoice.status === 'APPROVED') && (
            <Tooltip content="Record Payment">
              <Button
                icon={CashDollarIcon}
                variant="plain"
                tone="success"
                onClick={() => {
                  setSelectedInvoice(invoice);
                  setPaymentForm(prev => ({ ...prev, paymentMethod: invoice?.brand?.invoicePaymentMethod || "" }))
                  setPaymentModalActive(true);
                }}
                accessibilityLabel="Record payment"
              />
            </Tooltip>
          )}
          {(invoice.status === 'DRAFT' || invoice.status === 'CANCELLED') && (
            <Tooltip content="Delete">
              <Button
                icon={DeleteIcon}
                variant="plain"
                tone="critical"
                onClick={() => handleDeleteInvoice(invoice.id)}
                accessibilityLabel="Delete invoice"
              />
            </Tooltip>
          )}
        </InlineStack>
      </IndexTable.Cell>
    </IndexTable.Row>
  ));

  return (
    <Page
      title="Invoices"
      subtitle="Manage and send invoices to brands (auto-generated monthly)"
      primaryAction={{
        content: 'Trigger Monthly Invoices',
        icon: CalendarIcon,
        onAction: () => setGenerateModalActive(true),
      }}
      secondaryActions={[
        {
          content: 'Refresh',
          icon: RefreshIcon,
          onAction: fetchInvoices,
        }
      ]}
      fullWidth
    >
      <Layout>
        {error && (
          <Layout.Section>
            <Banner tone="critical" onDismiss={() => setError('')}>
              {error}
            </Banner>
          </Layout.Section>
        )}

        {successMessage && (
          <Layout.Section>
            <Banner tone="success" onDismiss={() => setSuccessMessage('')}>
              {successMessage}
            </Banner>
          </Layout.Section>
        )}

        {/* Filters */}
        <Layout.Section>
          <Card>
            <Box padding="400">
              <InlineStack gap="400" wrap={false}>
                <Box minWidth="200px">
                  <Select
                    label="Brand"
                    options={brandOptions}
                    value={brandFilter}
                    onChange={(value) => {
                      setBrandFilter(value);
                      setCurrentPage(1);
                    }}
                  />
                </Box>
                <Box minWidth="150px">
                  <Select
                    label="Status"
                    options={statusOptions}
                    value={statusFilter}
                    onChange={(value) => {
                      setStatusFilter(value);
                      setCurrentPage(1);
                    }}
                  />
                </Box>
                <Box minWidth="180px">
                  <Select
                    label="Month"
                    options={monthOptions}
                    value={monthFilter}
                    onChange={(value) => {
                      setMonthFilter(value);
                      setCurrentPage(1);
                    }}
                  />
                </Box>
              </InlineStack>
            </Box>
          </Card>
        </Layout.Section>

        {/* Invoice Table */}
        <Layout.Section>
          <Card>
            {loading ? (
              <Box padding="600">
                <InlineStack align="center" blockAlign="center" gap="400">
                  <Spinner size="small" />
                  <Text>Loading invoices...</Text>
                </InlineStack>
              </Box>
            ) : invoices.length === 0 ? (
              <EmptyState
                heading="No invoices found"
                image=""
                action={{
                  content: 'Trigger Monthly Invoices',
                  onAction: () => setGenerateModalActive(true),
                }}
              >
                <p>Invoices are automatically generated on the 1st of each month for all configured brands.</p>
              </EmptyState>
            ) : (
              <IndexTable
                resourceName={{ singular: 'invoice', plural: 'invoices' }}
                itemCount={invoices.length}
                selectable={false}
                headings={[
                  { title: 'Invoice #' },
                  { title: 'Brand' },
                  { title: 'Status' },
                  { title: 'Period' },
                  { title: 'Amount' },
                  { title: 'Issue Date' },
                  { title: 'Due Date' },
                  { title: 'Actions' },
                ]}
                pagination={{
                  hasNext: currentPage < totalPages,
                  hasPrevious: currentPage > 1,
                  onNext: () => setCurrentPage(p => p + 1),
                  onPrevious: () => setCurrentPage(p => p - 1),
                }}
              >
                {rowMarkup}
              </IndexTable>
            )}
          </Card>
        </Layout.Section>
      </Layout>

      {/* Trigger Monthly Invoices Modal */}
      <Modal
        open={generateModalActive}
        onClose={() => setGenerateModalActive(false)}
        title="Trigger Monthly Invoice Generation"
        primaryAction={{
          content: 'Trigger Generation',
          onAction: handleTriggerMonthlyInvoices,
          loading: actionLoading,
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => setGenerateModalActive(false),
          }
        ]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            <Text>
              This will trigger invoice generation for all brands with complete invoice configuration.
              Invoices are generated from existing billing reports (optimized, no recalculation).
            </Text>

            <FormLayout>
              <TextField
                label="Month (optional)"
                type="month"
                value={triggerForm.monthString}
                onChange={(value) => setTriggerForm(prev => ({ ...prev, monthString: value }))}
                helpText="Leave empty to generate for the previous month. Format: YYYY-MM"
                placeholder="e.g., 2024-12"
              />
            </FormLayout>

            <Banner tone="info">
              <Text>
                Invoices are automatically generated on the 1st of each month. Use this to manually trigger generation if needed.
              </Text>
            </Banner>
          </BlockStack>
        </Modal.Section>
      </Modal>

      {/* Record Payment Modal */}
      <Modal
        open={paymentModalActive}
        onClose={() => setPaymentModalActive(false)}
        title={`Record Payment - ${selectedInvoice?.invoiceNumber || ''}`}
        primaryAction={{
          content: 'Record Payment',
          icon: CashDollarIcon,
          onAction: handleRecordPayment,
          loading: actionLoading,
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => setPaymentModalActive(false),
          }
        ]}
      >
        <Modal.Section>
          <FormLayout>
            <TextField
              label="Payment Method"
              value={paymentForm.paymentMethod}
              onChange={(value) => setPaymentForm(prev => ({ ...prev, paymentMethod: value }))}
              placeholder="e.g., Bank Transfer, Check, Wire Transfer"
            />

            <TextField
              label="Reference Number"
              value={paymentForm.reference}
              onChange={(value) => setPaymentForm(prev => ({ ...prev, reference: value }))}
              placeholder="Transaction ID, check number, etc."
            />
          </FormLayout>
        </Modal.Section>
      </Modal>

      {/* Invoice Details Modal */}
      <Modal
        size='fullScreen'
        open={detailsModalActive}
        onClose={() => {
          setDetailsModalActive(false);
          setSelectedInvoice(null);
        }}
        title={`Invoice Preview - ${selectedInvoice?.invoiceNumber || ''}`}
        primaryAction={
          selectedInvoice?.status === 'DRAFT' ? {
            content: 'Approve Invoice',
            icon: CheckIcon,
            onAction: () => handleApproveInvoice(selectedInvoice.id),
            loading: actionLoading,
          } : selectedInvoice?.status === 'APPROVED' ? {
            content: 'Send Invoice',
            icon: EmailIcon,
            onAction: () => {
              setDetailsModalActive(false);
              setSendModalActive(true);
            },
          } : undefined
        }
        secondaryActions={[
          {
            content: pdfDownloadingId === selectedInvoice?.id ? 'Downloading...' : 'Download PDF',
            icon: ReceiptIcon,
            onAction: () => handleDownloadPdf(selectedInvoice?.id, selectedInvoice?.invoiceNumber),
            loading: pdfDownloadingId === selectedInvoice?.id,
            disabled: pdfDownloadingId !== null,
          },
          ...(selectedInvoice?.status !== 'PAID' && selectedInvoice?.status !== 'CANCELLED' ? [{
            content: 'Cancel Invoice',
            icon: DeleteIcon,
            destructive: true,
            onAction: () => handleCancelInvoice(selectedInvoice?.id),
          }] : []),
          {
            content: 'Close',
            onAction: () => {
              setDetailsModalActive(false);
              setSelectedInvoice(null);
            },
          }
        ]}
      >
        {selectedInvoice && (
          <Modal.Section>
            <div style={{ margin: '0 auto', padding: '30px 20px' }}>
              <BlockStack gap="800">
                {/* Status Badge */}
                <InlineStack gap="400" align="space-between">
                  {getStatusBadge(selectedInvoice.status)}
                </InlineStack>

                {/* Invoice Header - Matching PDF Layout */}
                <Box background="bg-surface-secondary" padding="800" borderRadius="300">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '60px' }}>
                    {/* Left Side - Logo and Invoice Info */}
                    <div style={{ flex: '1' }}>
                      {/* 2W Logo */}
                      <div style={{ marginBottom: '24px' }}>
                        <img src={`${import.meta.env.VITE_API_BASE_URL}/assets/logo`} alt="2W" style={{ height: '50px', width: 'auto' }} />
                      </div>

                      {/* Invoice Details */}
                      <div style={{ fontSize: '15px', lineHeight: '2' }}>
                        <p style={{ margin: '0 0 8px 0' }}><strong>Invoice No. {selectedInvoice.invoiceNumber}</strong></p>
                        <p style={{ margin: '0 0 8px 0' }}>Issue date: {formatDate(selectedInvoice.issueDate)}</p>
                        <p style={{ margin: '0 0 8px 0' }}>Due date: {formatDate(selectedInvoice.dueDate)}</p>
                        <p style={{ margin: '0' }}>Invoice Period: {formatDate(selectedInvoice.periodStart)} to {formatDate(selectedInvoice.periodEnd)}</p>
                      </div>
                    </div>
                    {/* Company Info - Left aligned */}
                    <div style={{ textAlign: 'left', fontSize: '15px', lineHeight: '2', flex: '1' }}>
                      {/* Company Address Block */}
                      <div style={{ marginBottom: '20px' }}>
                        <p style={{ margin: '0 0 8px 0', fontWeight: '600', fontSize: '16px' }}>{selectedInvoice.companyName || selectedInvoice.brand?.name || '-'}</p>
                        {selectedInvoice.companyAddress && (
                          <p style={{ margin: '0 0 8px 0', whiteSpace: 'pre-line' }}>{selectedInvoice.companyAddress}</p>
                        )}
                        {selectedInvoice.companyPhone && (
                          <p style={{ margin: '0' }}>{selectedInvoice.companyPhone}</p>
                        )}
                      </div>


                      {/* Company Registration Block */}
                      <div>
                        <p style={{ margin: '0 0 8px 0' }}>
                          {selectedInvoice.companyCode && ` (${selectedInvoice.companyCode})`}
                        </p>
                        {selectedInvoice.registrationNumber && (
                          <p style={{ margin: '0 0 8px 0' }}>Company Registration Number: {selectedInvoice.registrationNumber}</p>
                        )}
                        {selectedInvoice.taxNumber && (
                          <p style={{ margin: '0' }}>Tax number: {selectedInvoice.taxNumber}</p>
                        )}
                      </div>
                    </div>
                  </div>
                </Box>

                <Divider />

                {/* Invoice Summary - Fees Owed to 2W (Clean 2-column table) */}
                <BlockStack gap="500">
                  <Text variant="headingMd" fontWeight="semibold">Invoice Summary</Text>
                  <Box background="bg-surface-secondary" padding="600" borderRadius="200">
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '15px'}}>
                        <thead>
                          <tr style={{ borderBottom: '2px solid #e1e3e5' }}>
                            <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: '600', width: '70%' }}>Description</th>
                            <th style={{ textAlign: 'right', padding: '8px 12px', fontWeight: '600', width: '30%' }}>Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr style={{ borderBottom: '1px solid #e1e3e5' }}>
                            <td style={{ padding: '8px 12px' }}>Commission Fees</td>
                            <td style={{ textAlign: 'right', padding: '8px 12px' }}>{formatCurrency(selectedInvoice.commissionOnOrders)}</td>
                          </tr>
                          <tr style={{ borderBottom: '1px solid #e1e3e5' }}>
                            <td style={{ padding: '8px 12px' }}>Shipping Fees</td>
                            <td style={{ textAlign: 'right', padding: '8px 12px' }}>{formatCurrency(selectedInvoice.totalShippingFees)}</td>
                          </tr>
                          <tr style={{ borderBottom: '1px solid #e1e3e5' }}>
                            <td style={{ padding: '8px 12px' }}>Return Fees</td>
                            <td style={{ textAlign: 'right', padding: '8px 12px' }}>{formatCurrency(selectedInvoice.totalReturnFees)}</td>
                          </tr>
                          <tr style={{ borderBottom: '1px solid #e1e3e5' }}>
                            <td style={{ padding: '8px 12px' }}>Adjustments</td>
                            <td style={{ textAlign: 'right', padding: '8px 12px' }}>
                              {formatCurrency(selectedInvoice.commissionAdjustments || 0)}
                            </td>
                          </tr>
                          <tr style={{ borderBottom: '1px solid #ddd' }}>
                            <td style={{ padding: '12px', fontWeight: '600' }}>Subtotal</td>
                            <td style={{ textAlign: 'right', padding: '12px', fontWeight: '600' }}>
                              {formatCurrency(
                                (parseFloat(selectedInvoice.commissionOnOrders) || 0) +
                                (parseFloat(selectedInvoice.totalShippingFees) || 0) +
                                (parseFloat(selectedInvoice.totalReturnFees) || 0) +
                                (parseFloat(selectedInvoice.commissionAdjustments) || 0)
                              )}
                            </td>
                          </tr>
                          {(selectedInvoice.vatRate || 0) > 0 && (
                            <tr style={{ borderBottom: '1px solid #e1e3e5' }}>
                              <td style={{ padding: '8px 12px' }}>VAT ({selectedInvoice.vatRate}%)</td>
                              <td style={{ textAlign: 'right', padding: '8px 12px' }}>{formatCurrency(selectedInvoice.vatAmount || 0)}</td>
                            </tr>
                          )}
                          <tr style={{ backgroundColor: '#f6f6f7', fontWeight: '700', borderTop: '2px solid #333' }}>
                            <td style={{ padding: '12px', fontSize: '15px' }}>TOTAL PAYABLE TO 2W</td>
                            <td style={{ textAlign: 'right', padding: '12px', fontSize: '15px' }}>{formatCurrency(selectedInvoice.totalAmount)}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </Box>
                </BlockStack>

                {/* Marketplace-Level Breakdown - Net payout explanation */}
                {selectedInvoice.marketplaceBreakdown && selectedInvoice.marketplaceBreakdown.length > 0 && (
                  <>
                    <Divider />
                    <BlockStack gap="500">
                      <Text variant="headingMd" fontWeight="semibold">Marketplace-Level Breakdown</Text>
                      <Box background="bg-surface-secondary" padding="600" borderRadius="200">
                        <div style={{ overflowX: 'auto' }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', minWidth: '1000px' }}>
                            <thead>
                              <tr style={{ borderBottom: '2px solid #e1e3e5' }}>
                                <th style={{ textAlign: 'left', padding: '8px 8px', fontWeight: '600' }}>Marketplace</th>
                                <th style={{ textAlign: 'right', padding: '8px 8px', fontWeight: '600' }}>Orders</th>
                                <th style={{ textAlign: 'right', padding: '8px 8px', fontWeight: '600' }}>Refunds</th>
                                <th style={{ textAlign: 'right', padding: '8px 8px', fontWeight: '600' }}>Payable Orders</th>
                                <th style={{ textAlign: 'right', padding: '8px 8px', fontWeight: '600' }}>Commission</th>
                                <th style={{ textAlign: 'right', padding: '8px 8px', fontWeight: '600' }}>Shipping</th>
                                <th style={{ textAlign: 'right', padding: '8px 8px', fontWeight: '600' }}>Return Fees</th>
                                <th style={{ textAlign: 'right', padding: '8px 8px', fontWeight: '600' }}>Adjustments</th>
                                <th style={{ textAlign: 'right', padding: '8px 8px', fontWeight: '600' }}>Net to Merchant</th>
                              </tr>
                            </thead>
                            <tbody>
                              {selectedInvoice.marketplaceBreakdown.map((mp, idx) => {
                                const mpGrossSales = parseFloat(mp.grossSales) || 0;
                                const mpRefunds = parseFloat(mp.refundAmount || mp.refunds) || 0;
                                const mpPayableOrders = mpGrossSales - mpRefunds;
                                const mpCommission = parseFloat(mp.netCommission || mp.commission) || 0;
                                const mpShipping = parseFloat(mp.shippingFees) || 0;
                                const mpReturnFees = parseFloat(mp.returnFees) || 0;
                                const mpNetToMerchant = mpPayableOrders - mpCommission - mpShipping - mpReturnFees;
                                return (
                                  <tr key={`mp-${idx}`} style={{ borderBottom: '1px solid #e1e3e5' }}>
                                    <td style={{ padding: '8px 8px' }}>{mp.marketplace}</td>
                                    <td style={{ textAlign: 'right', padding: '8px 8px' }}>{formatCurrency(mpGrossSales)}</td>
                                    <td style={{ textAlign: 'right', padding: '8px 8px' }}>{formatNegativeCurrency(mpRefunds)}</td>
                                    <td style={{ textAlign: 'right', padding: '8px 8px' }}>{formatCurrency(mpPayableOrders)}</td>
                                    <td style={{ textAlign: 'right', padding: '8px 8px' }}>{formatNegativeCurrency(mpCommission)}</td>
                                    <td style={{ textAlign: 'right', padding: '8px 8px' }}>{formatNegativeCurrency(mpShipping)}</td>
                                    <td style={{ textAlign: 'right', padding: '8px 8px' }}>{formatNegativeCurrency(mpReturnFees)}</td>
                                    <td style={{ textAlign: 'right', padding: '8px 8px' }}></td>
                                    <td style={{ textAlign: 'right', padding: '8px 8px' }}>{formatCurrency(mpNetToMerchant)}</td>
                                  </tr>
                                );
                              })}
                              {/* ADJUSTMENTS */}
                              <tr>
                                <td></td>
                                <td></td>
                                <td></td>
                                <td></td>
                                <td></td>
                                <td></td>
                                <td></td>
                                <td style={{ textAlign: 'right', padding: '10px 8px' }}>
                                  USD {(selectedInvoice.commissionAdjustments || 0) < 0 ? '' : '-'}{Math.abs(selectedInvoice.commissionAdjustments || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </td>
                                <td style={{ textAlign: 'right', padding: '10px 8px' }}>
                                  USD {(selectedInvoice.commissionAdjustments || 0) < 0 ? '' : '-'}{Math.abs(selectedInvoice.commissionAdjustments || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </td>
                              </tr>
                              {/* TOTALS Row */}
                              <tr style={{ borderTop: '2px solid #333', fontWeight: '600' }}>
                                <td style={{ padding: '10px 8px' }}><strong>TOTALS</strong></td>
                                <td style={{ textAlign: 'right', padding: '10px 8px' }}><strong>{formatCurrency(selectedInvoice.grossSales)}</strong></td>
                                <td style={{ textAlign: 'right', padding: '10px 8px' }}><strong>{formatNegativeCurrency(selectedInvoice.refundAmount)}</strong></td>
                                <td style={{ textAlign: 'right', padding: '10px 8px' }}><strong>{formatCurrency((parseFloat(selectedInvoice.grossSales) || 0) - (parseFloat(selectedInvoice.refundAmount) || 0))}</strong></td>
                                <td style={{ textAlign: 'right', padding: '10px 8px' }}><strong>{formatNegativeCurrency(selectedInvoice.commissionOnOrders)}</strong></td>
                                <td style={{ textAlign: 'right', padding: '10px 8px' }}><strong>{formatNegativeCurrency(selectedInvoice.totalShippingFees)}</strong></td>
                                <td style={{ textAlign: 'right', padding: '10px 8px' }}><strong>{formatNegativeCurrency(selectedInvoice.totalReturnFees)}</strong></td>
                                <td style={{ textAlign: 'right', padding: '10px 8px' }}>
                                  <strong>USD {(selectedInvoice.commissionAdjustments || 0) < 0 ? '' : '-'}{Math.abs(selectedInvoice.commissionAdjustments || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
                                </td>
                                <td style={{ textAlign: 'right', padding: '10px 8px' }}>
                                  <strong>{formatCurrency(
                                    ((parseFloat(selectedInvoice.grossSales) || 0) - (parseFloat(selectedInvoice.refundAmount) || 0)) -
                                    (parseFloat(selectedInvoice.commissionOnOrders) || 0) -
                                    (parseFloat(selectedInvoice.totalShippingFees) || 0) -
                                    (parseFloat(selectedInvoice.totalReturnFees) || 0) -
                                    (parseFloat(selectedInvoice.commissionAdjustments) || 0)
                                  )}</strong>
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>

                        {/* Amount to be transferred to merchant */}
                        <Box paddingBlockStart="500" paddingBlockEnd="200">
                          <div style={{ borderTop: '2px solid #333', paddingTop: '15px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Text variant="headingMd" fontWeight="bold">AMOUNT TO BE TRANSFERRED TO MERCHANT:</Text>
                            <Text variant="headingMd" fontWeight="bold">
                              {formatCurrency(
                                ((parseFloat(selectedInvoice.grossSales) || 0) - (parseFloat(selectedInvoice.refundAmount) || 0)) -
                                (parseFloat(selectedInvoice.commissionOnOrders) || 0) -
                                (parseFloat(selectedInvoice.totalShippingFees) || 0) -
                                (parseFloat(selectedInvoice.totalReturnFees) || 0) -
                                (parseFloat(selectedInvoice.commissionAdjustments) || 0)
                              )}
                            </Text>
                          </div>
                        </Box>

                        {/* Footnote */}
                        <Box paddingBlockStart="400">
                          <Text variant="bodySm" tone="subdued" fontStyle="italic">
                            (3) Includes refunds for previous periods and the current period. Refund processing date determines the reporting period.
                          </Text>
                        </Box>
                      </Box>
                    </BlockStack>
                  </>
                )}

                {/* Payment Info */}
                {selectedInvoice.status === 'PAID' && (
                  <>
                    <Divider />
                    <BlockStack gap="300">
                      <Text variant="headingSm">Payment Information</Text>
                      <InlineStack gap="600">
                        <BlockStack gap="100">
                          <Text variant="bodySm" tone="subdued">Payment Date</Text>
                          <Text variant="bodyMd">{formatDate(selectedInvoice.paidAt)}</Text>
                        </BlockStack>
                        <BlockStack gap="100">
                          <Text variant="bodySm" tone="subdued">Payment Method</Text>
                          <Text variant="bodyMd">{selectedInvoice.paymentMethodText || '-'}</Text>
                        </BlockStack>
                        <BlockStack gap="100">
                          <Text variant="bodySm" tone="subdued">Reference</Text>
                          <Text variant="bodyMd">{selectedInvoice.paymentReference || '-'}</Text>
                        </BlockStack>
                      </InlineStack>
                    </BlockStack>
                  </>
                )}

                {/* Bank Details (masked for security) */}
                {(selectedInvoice.bankAccountNumber || selectedInvoice.routingNumber) && (
                  <>
                    <Divider />
                    <BlockStack gap="300">
                      <Text variant="headingSm">Bank Details</Text>
                      <InlineStack gap="600">
                        <BlockStack gap="100">
                          <Text variant="bodySm" tone="subdued">Account Number (BAN)</Text>
                          <Text variant="bodyMd">{maskAccountNumber(selectedInvoice.bankAccountNumber)}</Text>
                        </BlockStack>
                        <BlockStack gap="100">
                          <Text variant="bodySm" tone="subdued">Routing Number (ABA)</Text>
                          <Text variant="bodyMd">{maskAccountNumber(selectedInvoice.routingNumber)}</Text>
                        </BlockStack>
                      </InlineStack>
                      <Text variant="bodySm" tone="subdued">
                        Full bank details are available in the PDF invoice
                      </Text>
                    </BlockStack>
                  </>
                )}
              </BlockStack>
            </div>
          </Modal.Section>
        )}
      </Modal>
    </Page >
  );
}
